import { createFileRoute } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/danger/ConfirmDialog';
import { ConsequencePanel } from '@/components/danger/ConsequencePanel';
import { ActionGroup, DangerButton } from '@/components/danger/DangerButton';
import type { Consequence } from '@/components/danger/danger-tiers';
import { ActivityTimeline, type ActivityItem } from '@/components/data/ActivityTimeline';
import { DataTable, type Column, type SortState } from '@/components/data/DataTable';
import { LedgerTable, type LedgerEntry } from '@/components/data/LedgerTable';
import { MediaUploadCard } from '@/components/data/MediaUploadCard';
import { TriageRow, type TriageItem } from '@/components/data/TriageRow';
import { PageHeader } from '@/components/layout/PageHeader';
import { CopyableId } from '@/components/primitives/CopyableId';
import { EntityChip } from '@/components/primitives/EntityChip';
import { LiveDataIndicator } from '@/components/primitives/LiveDataIndicator';
import { StateChip } from '@/components/primitives/StateChip';
import { EmptyState } from '@/components/states/EmptyState';
import { PermissionDenied } from '@/components/states/ErrorState';
import { SkeletonCards, SkeletonTable } from '@/components/states/Skeleton';
import type { DangerTier } from '@/lib/permissions';

export const Route = createFileRoute('/design-system')({
  component: DesignSystemScreen,
});

const COLOUR_GROUPS = [
  { label: 'Surfaces', tokens: ['--bg', '--bg-alt', '--card', '--elev', '--high'] },
  { label: 'Borders', tokens: ['--bd', '--bd-s'] },
  { label: 'Text', tokens: ['--tx', '--tx2', '--tx3'] },
  {
    label: 'Semantic',
    tokens: ['--brand', '--gold', '--ok', '--warn', '--err', '--info', '--epic'],
  },
];

const STATE_SAMPLES = [
  'upcoming',
  'active',
  'processing',
  'complete',
  'in_progress',
  'paused',
  'on_the_clock',
  'locking',
  'completed',
  'pending',
  'opted_in',
  'opted_out',
  'sat_out',
  'expired',
  'waiting',
  'delayed',
  'failed',
  'published',
  'scheduled',
];

const LOCKED_WEEK_CONSEQUENCES: Consequence[] = [
  {
    text: 'Week 7 is locked and already scored. Published results will change.',
    severity: 'critical',
  },
  { text: 'Scoring will be re-enqueued for affected matchups', severity: 'critical', count: 8 },
  { text: 'Standings will be recomputed for every squad in the league', severity: 'warning' },
  {
    text: 'The weekly stat cache and lineup-lock cache will be invalidated',
    severity: 'warning',
  },
  { text: 'An audit record will be written with your name and reason', severity: 'info' },
];

/**
 * Live reference for the component system.
 *
 * A working surface rather than a screenshot: every control here is the same
 * component the real screens import, so a regression shows up on this page before
 * it reaches an operator mid-incident.
 */
function DesignSystemScreen() {
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Tokens, components & rules"
        title="Design system"
        meta="Every control below is the component the real screens import."
      />

      <Section title="Danger tiers">
        <p className="text-tx2 mb-3 max-w-[68ch] text-[13px] leading-[1.6]">
          The tier drives colour, icon and confirmation choreography identically everywhere, so
          an operator learns it once. Tier 3 is deliberately never filled red in a list of
          actions — a wall of solid red trains people to ignore it. It becomes solid only at the
          final confirm step, where it is the one thing on screen.
        </p>
        <div className="flex flex-col gap-2.5">
          <ActionGroup tier={1}>
            <DangerButton tier={1}>Export CSV</DangerButton>
            <DangerButton tier={1}>Resend invite</DangerButton>
          </ActionGroup>
          <ActionGroup tier={2}>
            <DangerButton tier={2}>Save settings changes</DangerButton>
            <DangerButton tier={2}>Regenerate invite code</DangerButton>
            <DangerButton tier={2} loading>
              Saving
            </DangerButton>
            <DangerButton tier={2} disabled>
              Disabled
            </DangerButton>
          </ActionGroup>
          <ActionGroup tier={3} hint="bypasses the lock">
            <DangerButton tier={3}>Swap two players</DangerButton>
            <DangerButton tier={3}>Force the current pick</DangerButton>
            <DangerButton tier={3} loading>
              Applying
            </DangerButton>
            <DangerButton tier={3} disabled>
              Not permitted
            </DangerButton>
          </ActionGroup>
        </div>
      </Section>

      <Section title="Confirmation choreography">
        <p className="text-tx2 mb-3 max-w-[68ch] text-[13px] leading-[1.6]">
          The brief&apos;s worked example, end to end: swapping a roster player during a locked,
          already-scored week. On a phone the Tier 3 variant becomes a full-screen sheet, so a
          mis-tap cannot confirm it.
        </p>
        <ConfirmationDemos />
      </Section>

      <Section title="Consequence panel">
        <ConsequencePanel consequences={LOCKED_WEEK_CONSEQUENCES} />
      </Section>

      <Section title="State chips">
        <p className="text-tx2 mb-3 max-w-[68ch] text-[13px] leading-[1.6]">
          One colour language across week status, draft status, participation status and job
          status. Only genuinely-live states animate — motion has to mean something.
        </p>
        <div className="flex flex-wrap gap-2">
          {STATE_SAMPLES.map((state) => (
            <StateChip key={state} state={state} />
          ))}
        </div>
      </Section>

      <Section title="Entity chips">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-4">
            <EntityChip
              kind="league"
              id="507f1f77bcf86cd799439011"
              name="Kappa Sig Fall Classic"
              secondary="KSFC24"
            />
            <EntityChip
              kind="user"
              id="507f1f77bcf86cd799439012"
              name="Brandon Meyer"
              secondary="@brickwall_brandon"
            />
            <EntityChip
              kind="squad"
              id="507f1f77bcf86cd799439013"
              name="Touchdown Bandits"
              secondary="6 members"
            />
          </div>
          <div className="border-bd border-t pt-3">
            <EntityChip
              variant="expanded"
              kind="league"
              id="507f1f77bcf86cd799439011"
              name="Kappa Sig Fall Classic"
              secondary="KSFC24 · football · 12 teams"
            />
          </div>
        </div>
      </Section>

      <Section title="Copyable identifiers">
        <div className="flex flex-wrap gap-2">
          <CopyableId value="507f1f77bcf86cd799439011" label="League ID" />
          <CopyableId value="507f1f77bcf86cd799439011" label="League ID" truncate />
          <CopyableId value="KSFC24" label="Invite code" />
        </div>
      </Section>

      <Section title="Live-data indicator">
        <LiveDataDemo />
      </Section>

      <Section title="Loading skeletons">
        <p className="text-tx2 mb-3 max-w-[68ch] text-[13px] leading-[1.6]">
          Skeletons match the final layout so the screen does not reflow under the
          operator&apos;s cursor as data lands — which on a Tier 3 control is how mis-taps
          happen.
        </p>
        <div className="grid gap-3 lg:grid-cols-2">
          <SkeletonTable rows={4} columns={5} />
          <SkeletonCards count={3} />
        </div>
      </Section>

      <Section title="Empty, error & permission states">
        <div className="grid gap-3 lg:grid-cols-2">
          <EmptyState
            title="No open issues"
            description="Nothing in this league needs attention right now."
          />
          <PermissionDenied capability="leagues.override" />
        </div>
      </Section>

      <Section title="Data table">
        <p className="text-tx2 mb-3 max-w-[68ch] text-[13px] leading-[1.6]">
          Sortable, bulk-selectable, sticky header. Each column declares where it goes when the
          table becomes cards on mobile — <code className="mesh-mono">primary</code> is the card
          headline, <code className="mesh-mono">secondary</code> the chip row, and{' '}
          <code className="mesh-mono">detail</code> sits behind an expand. Narrow the window
          past 768px to see it. Bulk actions are Tier 1 only.
        </p>
        <LeagueTableDemo />
      </Section>

      <Section title="Ledger table">
        <p className="text-tx2 mb-3 max-w-[68ch] text-[13px] leading-[1.6]">
          Append-only, with no edit affordance at all — not a disabled one. Balances are shown
          as recorded, never recomputed from the deltas; a mismatch is an integrity problem the
          operator must see rather than have the UI paper over. Negative deltas are muted, not
          red — red means danger.
        </p>
        <LedgerTable entries={LEDGER_ENTRIES} unit="tokens" label="token ledger" />
      </Section>

      <Section title="Triage rows">
        <p className="text-tx2 mb-3 max-w-[68ch] text-[13px] leading-[1.6]">
          The attention and moderation queues. Age is prominent and tabular because &ldquo;how
          long has this been broken&rdquo; drives ordering more than what broke. Inline actions
          are Tier 1 and Tier 2 only — a queue is no place for an irreversible control.
        </p>
        <div className="flex flex-col gap-2">
          {TRIAGE_ITEMS.map((item) => (
            <TriageRow
              key={item.id}
              item={item}
              actions={<DangerButton tier={2}>Resume</DangerButton>}
            />
          ))}
        </div>
      </Section>

      <Section title="Activity timeline">
        <ActivityTimeline items={ACTIVITY_ITEMS} />
      </Section>

      <Section title="Media upload lifecycle">
        <p className="text-tx2 mb-3 max-w-[68ch] text-[13px] leading-[1.6]">
          Source art → WebP → MP4 → CDN. Every stage is a state a content manager can be looking
          at, including the failures. The error is shown verbatim and monospaced so it can be
          pasted into a ticket — &ldquo;something went wrong&rdquo; wastes a day.
        </p>
        <div className="grid gap-3 lg:grid-cols-2">
          <MediaUploadCard
            state={{ stage: 'idle' }}
            requirements={[
              'PNG or SVG',
              '2048 × 2048',
              'transparent background',
              'under 12 MB',
            ]}
          />
          <MediaUploadCard
            state={{
              stage: 'uploading',
              fileName: 'rage-cup-v3.png',
              progressLabel: '8.4 / 11.2 MB',
              progressPercent: 75,
            }}
          />
          <MediaUploadCard
            state={{
              stage: 'processing',
              fileName: 'rage-cup-v3.png',
              stageLabel: 'Converting to WebP and MP4',
            }}
          />
          <MediaUploadCard
            state={{
              stage: 'failed',
              fileName: 'rage-cup-v3.png',
              errorMessage:
                'ffmpeg: dimensions 2048×1536 are not square\nexpected 1:1 aspect ratio, got 4:3',
              jobId: 'job media_88f21c',
              attemptLabel: 'attempt 2 of 3',
            }}
            onRetry={() => toast('Retrying conversion')}
            onReplace={() => toast('Choose another file')}
          />
        </div>
      </Section>

      <Section title="Colour tokens">
        <div className="flex flex-col gap-4">
          {COLOUR_GROUPS.map((group) => (
            <div key={group.label}>
              <div
                className="font-head text-tx3 mb-2 text-[11px] font-semibold uppercase"
                style={{ letterSpacing: '0.18em' }}
              >
                {group.label}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                {group.tokens.map((token) => (
                  <div
                    key={token}
                    className="border-bd bg-card overflow-hidden rounded-lg border"
                  >
                    <div
                      className="border-bd h-11 border-b"
                      style={{ background: `var(${token})` }}
                    />
                    <div className="px-2.5 py-2">
                      <div className="mesh-mono text-tx2 text-[11px]">{token}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Type scale">
        <div className="flex flex-col gap-3">
          <div>
            <TypeLabel>Barlow Condensed · headers, labels, table heads</TypeLabel>
            <div className="font-head text-tx text-[32px] font-semibold">Week 7 is locked</div>
          </div>
          <div>
            <TypeLabel>Outfit · body and form text</TypeLabel>
            <div className="text-tx2 text-[14px]">
              Week 7 is locked and scored. This swap will re-enqueue scoring for 8 matchups.
            </div>
          </div>
          <div>
            <TypeLabel>JetBrains Mono · IDs, codes, timestamps</TypeLabel>
            <div className="mesh-mono text-tx2 text-[13px]">507f1f77bcf86cd799439011</div>
          </div>
        </div>
      </Section>
    </div>
  );
}

/** Timestamps are captured once on mount so the render stays pure. */
function LiveDataDemo() {
  const [mountedAt] = useState(() => Date.now());

  return (
    <div className="flex flex-col gap-2.5">
      <LiveDataIndicator updatedAt={mountedAt} isLive onRefresh={() => toast('Refetching')} />
      <LiveDataIndicator updatedAt={mountedAt} isFetching isLive />
      <LiveDataIndicator
        updatedAt={mountedAt - 5 * 60_000}
        staleAfterMs={60_000}
        onRefresh={() => toast('Refetching')}
      />
    </div>
  );
}

interface DemoLeague {
  id: string;
  name: string;
  inviteCode: string;
  sport: string;
  filled: string;
  seasonStatus: string;
  week: number;
  weekStatus: string;
  commissioner: string;
  created: string;
}

const DEMO_LEAGUES: DemoLeague[] = [
  {
    id: '507f1f77bcf86cd799439011',
    name: 'Kappa Sig Fall Classic',
    inviteCode: 'KSFC24',
    sport: 'football',
    filled: '12 / 12',
    seasonStatus: 'active',
    week: 7,
    weekStatus: 'processing',
    commissioner: 'Brandon Meyer',
    created: '2026-08-14',
  },
  {
    id: '507f1f77bcf86cd799439012',
    name: 'Tempe Sunday League',
    inviteCode: 'TSL77',
    sport: 'football',
    filled: '9 / 10',
    seasonStatus: 'active',
    week: 7,
    weekStatus: 'active',
    commissioner: 'Dana Whitfield',
    created: '2026-08-02',
  },
  {
    id: '507f1f77bcf86cd799439013',
    name: 'Hoops Winter Invitational',
    inviteCode: 'HWI09',
    sport: 'basketball',
    filled: '6 / 8',
    seasonStatus: 'upcoming',
    week: 1,
    weekStatus: 'upcoming',
    commissioner: 'Marcus Lyle',
    created: '2026-09-20',
  },
];

function LeagueTableDemo() {
  const [sort, setSort] = useState<SortState>({ columnId: 'name', direction: 'asc' });
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const columns: Column<DemoLeague>[] = [
    {
      id: 'name',
      header: 'League',
      sortable: true,
      width: '1.6fr',
      mobile: 'primary',
      cell: (league) => (
        <EntityChip
          kind="league"
          id={league.id}
          name={league.name}
          secondary={league.inviteCode}
        />
      ),
    },
    {
      id: 'sport',
      header: 'Sport',
      width: '110px',
      mobile: 'secondary',
      cell: (league) => <span className="text-tx2">{league.sport}</span>,
    },
    {
      id: 'filled',
      header: 'Filled',
      width: '90px',
      mobile: 'secondary',
      cell: (league) => <span className="mesh-mono text-tx2">{league.filled}</span>,
    },
    {
      id: 'week',
      header: 'Week',
      width: '150px',
      mobile: 'secondary',
      cell: (league) => (
        <div className="flex items-center gap-1.5">
          <span className="mesh-mono text-tx3">W{league.week}</span>
          <StateChip state={league.weekStatus} size="sm" />
        </div>
      ),
    },
    {
      id: 'season',
      header: 'Season',
      width: '120px',
      mobile: 'detail',
      cell: (league) => <StateChip state={league.seasonStatus} size="sm" />,
    },
    {
      id: 'commissioner',
      header: 'Commissioner',
      width: '1fr',
      mobile: 'detail',
      cell: (league) => <span className="text-tx2">{league.commissioner}</span>,
    },
    {
      id: 'created',
      header: 'Created',
      width: '110px',
      sortable: true,
      align: 'right',
      mobile: 'detail',
      cell: (league) => <span className="mesh-mono text-tx3">{league.created}</span>,
    },
  ];

  const sorted = [...DEMO_LEAGUES].sort((a, b) => {
    const key = sort.columnId === 'created' ? 'created' : 'name';
    const result = a[key].localeCompare(b[key]);
    return sort.direction === 'asc' ? result : -result;
  });

  return (
    <DataTable
      label="Leagues"
      columns={columns}
      rows={sorted}
      rowKey={(league) => league.id}
      sort={sort}
      onSortChange={setSort}
      selection={{
        selected,
        onChange: setSelected,
        // Tier 1 only. Bulk selection must never reach an override.
        actions: <DangerButton tier={1}>Export CSV</DangerButton>,
      }}
    />
  );
}

const LEDGER_ENTRIES: LedgerEntry[] = [
  {
    id: 'l1',
    occurredAt: '2026-10-18 14:07:22Z',
    delta: -250,
    balance: 1150,
    reason: 'Unlocked mascot “Rage Cup”',
    sourceType: 'shop_unlock',
  },
  {
    id: 'l2',
    occurredAt: '2026-10-17 09:41:03Z',
    delta: 500,
    balance: 1400,
    reason: 'Admin grant — refund for failed taunt purchase',
    sourceType: 'admin_grant',
    actorName: 'J. McMahon',
  },
  {
    id: 'l3',
    occurredAt: '2026-10-12 20:15:44Z',
    delta: 900,
    balance: 900,
    reason: 'Token pack purchase',
    sourceType: 'revenuecat',
    externalId: 'rc_1f8a02c9',
  },
];

const TRIAGE_ITEMS: TriageItem[] = [
  {
    id: 't1',
    title: 'Draft stalled on the clock',
    subject: 'Kappa Sig Fall Classic · round 4, pick 7',
    age: '19m',
    severity: 'critical',
    state: 'stalled',
    to: '/leagues/507f1f77bcf86cd799439011/draft',
  },
  {
    id: 't2',
    title: 'Waivers past process time',
    subject: 'Tempe Sunday League · 4 pending claims',
    age: '3h 12m',
    severity: 'warning',
    state: 'delayed',
    to: '/leagues/507f1f77bcf86cd799439012/waivers',
  },
  {
    id: 't3',
    title: 'Week stuck in processing',
    subject: 'Hoops Winter Invitational · week 1',
    age: '41m',
    severity: 'warning',
    state: 'processing',
    to: '/leagues/507f1f77bcf86cd799439013/weeks',
  },
];

const ACTIVITY_ITEMS: ActivityItem[] = [
  {
    id: 'a1',
    type: 'admin_intervention',
    occurredAt: '2026-10-18T14:12:00Z',
    dayLabel: 'Sunday, October 18',
    timeLabel: '14:12',
    summary: 'Forced the current pick for Touchdown Bandits',
    actorName: 'J. McMahon · Engineer',
    reason: 'Commissioner reported a stuck clock; forcing the pick to unblock the draft.',
  },
  {
    id: 'a2',
    type: 'draft_pick',
    occurredAt: '2026-10-18T14:09:00Z',
    dayLabel: 'Sunday, October 18',
    timeLabel: '14:09',
    summary: 'Touchdown Bandits drafted D. Adams (WR, GB)',
  },
  {
    id: 'a3',
    type: 'trade',
    occurredAt: '2026-10-17T19:02:00Z',
    dayLabel: 'Saturday, October 17',
    timeLabel: '19:02',
    summary: 'Trade accepted between Touchdown Bandits and Foam Finger FC',
  },
  {
    id: 'a4',
    type: 'join',
    occurredAt: '2026-10-17T11:20:00Z',
    dayLabel: 'Saturday, October 17',
    timeLabel: '11:20',
    summary: '@brickwall_brandon joined the league',
  },
];

function ConfirmationDemos() {
  const [openTier, setOpenTier] = useState<DangerTier | null>(null);
  const [isPending, setPending] = useState(false);

  function confirm(tier: DangerTier, reason: string) {
    setPending(true);
    // Stands in for the mutation. The real path sends an Idempotency-Key so a
    // retry after an uncertain failure collapses into the original action.
    setTimeout(() => {
      setPending(false);
      setOpenTier(null);
      toast.success(
        tier === 3 ? 'Roster swap applied — scoring re-enqueued' : 'Invite code regenerated',
        { description: reason || undefined },
      );
    }, 1200);
  }

  return (
    <div className="flex flex-wrap gap-2">
      <DangerButton tier={2} onClick={() => setOpenTier(2)}>
        Demo Tier 2 confirm
      </DangerButton>
      <DangerButton tier={3} onClick={() => setOpenTier(3)}>
        Demo Tier 3 — locked-week roster swap
      </DangerButton>

      <ConfirmDialog
        open={openTier === 2}
        onOpenChange={(open) => setOpenTier(open ? 2 : null)}
        tier={2}
        title="Regenerate invite code"
        summary="The current code KSFC24 stops working immediately. Anyone holding a share link will need the new code. Existing members are unaffected."
        confirmLabel="Regenerate code"
        isPending={isPending}
        onConfirm={(reason) => confirm(2, reason)}
      />

      <ConfirmDialog
        open={openTier === 3}
        onOpenChange={(open) => setOpenTier(open ? 3 : null)}
        tier={3}
        title="Swap two players during a locked week"
        summary={
          <>
            Moving <strong className="text-tx">A. Ekeler</strong> from WR2 to bench and{' '}
            <strong className="text-tx">D. Adams</strong> from bench to WR2 for{' '}
            <strong className="text-tx">Touchdown Bandits</strong> in week 7.
          </>
        }
        consequences={LOCKED_WEEK_CONSEQUENCES}
        typeToConfirmValue="Touchdown Bandits"
        typeToConfirmLabel="squad name"
        confirmLabel="Apply swap & re-score"
        isPending={isPending}
        onConfirm={(reason) => confirm(3, reason)}
      >
        <RosterDiff />
      </ConfirmDialog>
    </div>
  );
}

/** Compact before → after preview, with the affected slots highlighted. */
function RosterDiff() {
  const rows = [
    { slot: 'WR2', before: 'A. Ekeler', after: 'D. Adams', changed: true },
    { slot: 'BN', before: 'D. Adams', after: 'A. Ekeler', changed: true },
    { slot: 'QB', before: 'J. Allen', after: 'J. Allen', changed: false },
  ];

  return (
    <div className="border-bd overflow-hidden rounded-lg border">
      <div
        className="font-head border-bd bg-elev text-tx3 grid grid-cols-[56px_1fr_1fr] gap-2 border-b px-2.5 py-1.5 text-[10.5px] font-semibold uppercase"
        style={{ letterSpacing: '0.14em' }}
      >
        <span>Slot</span>
        <span>Before</span>
        <span>After</span>
      </div>
      {rows.map((row) => (
        <div
          key={row.slot}
          className="border-bd/60 grid grid-cols-[56px_1fr_1fr] gap-2 border-b px-2.5 py-2 text-[12.5px] last:border-b-0"
          style={{ background: row.changed ? 'rgba(var(--err-rgb), 0.06)' : undefined }}
        >
          <span className="mesh-mono text-tx3">{row.slot}</span>
          <span className={row.changed ? 'text-tx3 line-through' : 'text-tx2'}>
            {row.before}
          </span>
          <span className={row.changed ? 'text-tx font-medium' : 'text-tx2'}>{row.after}</span>
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-bd bg-card rounded-xl border p-3.5 md:p-4">
      <h2
        className="font-head text-tx mb-3 text-[15px] font-semibold uppercase"
        style={{ letterSpacing: '0.12em' }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function TypeLabel({ children }: { children: ReactNode }) {
  return (
    <div
      className="font-head text-tx3 mb-1 text-[10.5px] font-semibold uppercase"
      style={{ letterSpacing: '0.16em' }}
    >
      {children}
    </div>
  );
}
